import { useState, useCallback } from 'react'

export interface UseCounterOptions {
  /**
   * The initial count value
   * @default 0
   */
  initialValue?: number
  
  /**
   * The minimum allowed value
   */
  min?: number
  
  /**
   * The maximum allowed value
   */
  max?: number
  
  /**
   * The step value for increment/decrement
   * @default 1
   */
  step?: number
}

export interface UseCounterReturn {
  /** The current count value */
  count: number
  
  /** Increment the count by the step value */
  increment: () => void
  
  /** Decrement the count by the step value */
  decrement: () => void
  
  /** Reset the count to the initial value */
  reset: () => void
  
  /** Set the count to a specific value */
  set: (value: number) => void
  
  /** Whether the count is at the minimum value */
  isAtMin: boolean
  
  /** Whether the count is at the maximum value */
  isAtMax: boolean
}

/**
 * A hook for managing counter state with optional min/max constraints
 * 
 * @param options - Configuration options for the counter
 * @returns Counter state and controls
 * 
 * @example
 * ```tsx
 * const { count, increment, decrement, reset } = useCounter({
 *   initialValue: 0,
 *   min: 0,
 *   max: 10,
 *   step: 1
 * })
 * ```
 */
export function useCounter(options: UseCounterOptions = {}): UseCounterReturn {
  const {
    initialValue = 0,
    min = -Infinity,
    max = Infinity,
    step = 1,
  } = options

  const [count, setCount] = useState(initialValue)

  const increment = useCallback(() => {
    setCount((prev) => Math.min(prev + step, max))
  }, [step, max])

  const decrement = useCallback(() => {
    setCount((prev) => Math.max(prev - step, min))
  }, [step, min])

  const reset = useCallback(() => {
    setCount(initialValue)
  }, [initialValue])

  const set = useCallback(
    (value: number) => {
      setCount(Math.max(min, Math.min(max, value)))
    },
    [min, max]
  )

  const isAtMin = count <= min
  const isAtMax = count >= max

  return {
    count,
    increment,
    decrement,
    reset,
    set,
    isAtMin,
    isAtMax,
  }
}